import { BucketImage } from '../hooks/useBucketImage';
import { Organisation } from './organisation';
import { Address } from './types';

export enum Groups {
	All = '*',
	Student = 'students',
	Registrar = 'Registrar',
	AdmissionsOfficer = 'AdmissionsOfficers',
	Admin = 'Admins',
	University = 'University',
	VerificationAuthority = 'VerificationAuthority',
	VerificationOfficer = 'VerificationOfficer',
	Agent = 'Agent',
	HR = 'HR',
	BillingAdministrator = 'BillingAdministrator'
}

export enum RoleName {
	OrgnaisationAdmin = 'organisationAdmin',
	HR = 'hr',
	Registrar = 'registrar',
	AdmissionsOfficer = 'admissionsOfficer',
	Agent = 'agent',
	Verifier = 'verifier',
	Onboarding = 'onboarding',
	Student = 'student',
	Employee = 'employee',
	BillingAdministrator = 'billingAdministrator',
	VerifierOnlyOrg = 'verifierOnlyOrg',
	FamilyOrganisationAdmin = 'familyOrganisationAdmin',
	FamilyMember = 'familyMember'
}

// MFA setup is no longer part of sign-up — new accounts go straight to onboarding and are
// prompted to set up MFA on their next sign-in instead. Plan selection has likewise been
// removed: every account starts on the free tier and is offered an upgrade from the
// homepage, so sign-up now ends at the profile step.
export const signUpStepsNoMfa = ['Sign Up', 'Confirm', 'Profile'];

export const RoleDescription = {
	organisationAdmin:
		'Can utilize all functionalities, update school information/logo, add staff members, and control their functionalities.',
	hr: 'Can add existing employees, review candidate enquiries, and request documents from candidates.',
	registrar:
		'Can individually and bulk upload student users, verify documents, and manage Slate Integration (as well as other SFTPs).',
	admissionsOfficer: 'Can review candidate enquiries and request documents from candidates.',
	agent: 'Authorized representative for Individual wallet holder',
	verifier: 'Can verify documents.',
	onboarding:
		'Can individually and bulk upload employee users, create templates, and request documents from employees.',
	student: 'No staff role functions. Only ability is to utilize their individual wallet.',
	employee: 'No staff role functions. Only ability is to utilize their individual wallet.',
	billingAdministrator: 'Can manage billing and subscription information.',
	verifierOnlyOrg:
		'This verifier only has permissions to verify documents. To upgrade to a fully fledged organisation reach out to the app admin.',
	familyOrganisationAdmin: 'Parent/guardian who manages the family, family members, and their documents.',
	familyMember: 'Child or dependent in a family. Has individual wallet with parent oversight.'
};

/** Friendly labels for DynamoDB RoleName values (used in admin forms). */
export const RoleLabels: Record<RoleName, string> = {
	[RoleName.OrgnaisationAdmin]: 'Organisation Admin',
	[RoleName.HR]: 'HR',
	[RoleName.Registrar]: 'Registrar',
	[RoleName.AdmissionsOfficer]: 'Admissions Officer',
	[RoleName.Agent]: 'Agent',
	[RoleName.Verifier]: 'Verifier',
	[RoleName.Onboarding]: 'Onboarding',
	[RoleName.Student]: 'Student (Role)',
	[RoleName.Employee]: 'Employee',
	[RoleName.BillingAdministrator]: 'Billing Administrator',
	[RoleName.VerifierOnlyOrg]: 'Verifier Only Org',
	[RoleName.FamilyOrganisationAdmin]: 'Family Admin',
	[RoleName.FamilyMember]: 'Family Member'
};

export enum UserGroup {
	All = '*',
	User = 'user',
	Admin = 'Admins'
}

export type EducationStaffRole = Extract<
	RoleName,
	| RoleName.OrgnaisationAdmin
	| RoleName.HR
	| RoleName.Registrar
	| RoleName.AdmissionsOfficer
	| RoleName.BillingAdministrator
>;

export type EducationRole = EducationStaffRole | Extract<RoleName, RoleName.Student | RoleName.Employee>;

export const EducationStaffRoles = {
	OrgnaisationAdmin: RoleName.OrgnaisationAdmin,
	HR: RoleName.HR,
	Onboarding: RoleName.Onboarding,
	Registrar: RoleName.Registrar,
	AdmissionsOfficer: RoleName.AdmissionsOfficer,
	BillingAdministrator: RoleName.BillingAdministrator,
	VerifierOnlyOrg: RoleName.VerifierOnlyOrg
};

export const EducationRoles = {
	...EducationStaffRoles,
	Student: RoleName.Student,
	Employee: RoleName.Employee
};

export type CompanyStaffRole = Extract<
	RoleName,
	| RoleName.OrgnaisationAdmin
	| RoleName.HR
	| RoleName.Onboarding
	| RoleName.Verifier
	| RoleName.FamilyOrganisationAdmin
>;

export type CompanyRole = CompanyStaffRole | Extract<RoleName, RoleName.Employee | RoleName.FamilyMember>;

export const CompanyStaffRoles = {
	OrgnaisationAdmin: RoleName.OrgnaisationAdmin,
	HR: RoleName.HR,
	Onboarding: RoleName.Onboarding,
	Verifier: RoleName.Verifier,
	Agent: RoleName.Agent,
	BillingAdministrator: RoleName.BillingAdministrator,
	VerifierOnlyOrg: RoleName.VerifierOnlyOrg,
	FamilyOrganisationAdmin: RoleName.FamilyOrganisationAdmin
};

export const CompanyRoles = {
	...CompanyStaffRoles,
	Employee: RoleName.Employee,
	FamilyMember: RoleName.FamilyMember
};

export const HRRoles = [RoleName.HR, RoleName.Onboarding]; // Removed Employee role from HR roles

export const EducationHRRoles = [RoleName.HR, RoleName.Onboarding];

export const OrgViewOnlyPrivileges = [
	RoleName.HR,
	RoleName.AdmissionsOfficer,
	RoleName.Agent,
	RoleName.Verifier,
	RoleName.Onboarding,
	RoleName.Student,
	RoleName.Employee
];

export enum ApplicationRoles {
	admissionsOfficer = 'Admissions Officer',
	registrar = 'Registrar',
	hr = 'HR',
	organisationAdmin = 'Organization Admin',
	verifier = 'Verifier',
	employee = 'Employee',
	student = 'Student',
	familyOrganisationAdmin = 'Family Admin',
	familyMember = 'Family Member',
	agent = 'Agent',
	verifierOnlyOrg = 'Verifier Only Org',
	onboarding = 'Onboarding'
}
export interface ExternalReference {
	namespace: string;
	externalId: string;
	organisationId?: string;
}
export interface UserRole {
	userId?: string;
	organisationId?: string;
	roleName: RoleName;
	serialNumber?: string;
	externalSerialNumber?: string;
	agentId?: string; // agent is the role among an organisation. the roleName is Student and the agentId corresponds to the agent of that organisation.
	agentName?: string; // agent is the role among an organisation. the roleName is Student and the agentName corresponds to the agent of that organisation.
	roleDescription?: string;
}
/**
 * Canonical institution stages (PTEC-517) — jurisdiction-NEUTRAL codes; the
 * user-facing label localises per jurisdiction (UK "Secondary school", US
 * "Middle school", …) via utils/educationHistory. Stored values never change
 * with locale.
 */
export enum InstitutionType {
	Primary = 'primary',
	LowerSecondary = 'lowerSecondary',
	UpperSecondary = 'upperSecondary',
	College = 'college',
	University = 'university',
	Vocational = 'vocational',
	Other = 'other'
}

/** One school/institution in the user's education history (PTEC-517). */
export interface EducationHistoryEntry {
	institution: string;
	/** Canonical stage; label localises to the school's own country (below). */
	type?: InstitutionType;
	/** ISO country code of the school — drives the stage vocabulary per entry. */
	country?: string;
	/** Year finished; omitted = ongoing (treated as most recent when deriving). */
	endYear?: number;
	/**
	 * Directory record id (PTEC-643), `nces:{ncessch}` or `ipeds:{unitid}`.
	 *
	 * Present ONLY when the value was chosen from the school directory; absent for
	 * anything typed. That distinction is the point: the picker is freeSolo, so
	 * without this a picked school and a hand-typed one are the same string once
	 * saved, leaving nothing to canonicalise on, nothing to join to the directory,
	 * and only an unnormalised name to hand a partner at delivery (PTEC-521).
	 *
	 * The prefix identifies the source as well as the record, because a school can
	 * appear in both CCD and IPEDS.
	 */
	sourceId?: string;
	/** Subdivision (US state code) of the directory record. Absent when typed. */
	subdivision?: string;
}

export interface User {
	// fixed user fields
	id: string;
	groups: UserGroup[];
	email: string;
	emailVerified: boolean;
	terms: boolean;
	roles: UserRole[];
	organisations: Organisation[]; // Only organisations with a Staff Role

	// profile fields
	confirmed?: boolean; // true if profile form get filled
	firstName?: string;
	lastName?: string;
	preferredName?: string;
	phone?: string;
	currency?: string;
	language?: string;
	address?: {
		line1: string;
		line2?: string;
		city: string;
		state: string;
		country: string;
		zip: string;
		latitude?: string;
		longitude?: string;
		isoCountryCode?: string;
	};
	dateOfBirth?: string;
	citizenship?: string;
	/**
	 * Canonical education history (PTEC-517) — optional, user-curated. "Most
	 * recent school attended" is DERIVED (see utils/educationHistory), never
	 * stored; it is a Lead-Share transmittable field and disclosed as such.
	 */
	educationHistory?: EducationHistoryEntry[];
	profilePicture?: BucketImage;
	isLoading?: boolean;
	license?: string;
	stripeIdentityVerified?: StripeIdentityVerificationStatus;
	userMfaSettings?: string;
	totpSkipCount?: number;
	userMFASet?: boolean;

	// partner API fields
	externalReferences?: ExternalReference[];

	// additional details
	userCreatedDateTime?: string;
	// Admin users list only: the sign-in account (see UserAccount).
	account?: UserAccount | null;
	documentCount?: number;
	verificationCount?: number;
	organisationName?: string;

	// engagement / gamification
	loginStreakDays?: number;
	loginCount?: number;
}

/**
 * A user's sign-in account as an app admin sees it (GET /admins/users/:id/account). Cognito
 * never marks an invitation expired, so the server works out `inviteExpired` from when the
 * temporary password was last sent and the user pool's validity period.
 */
export type UserAccountStatus = 'active' | 'invited' | 'signUpIncomplete' | 'resetRequired' | 'disabled' | 'other';

export interface UserAccount {
	/** Whose account this is, so a late answer about someone else is not shown as theirs. */
	userId?: string;
	status: UserAccountStatus;
	cognitoStatus: string | null;
	createdAt: string | null;
	inviteSentAt?: string | null;
	inviteExpiresAt?: string | null;
	inviteExpired?: boolean;
	temporaryPasswordValidityDays?: number;
	// Returned after a resend.
	email?: string;
	/** Whether they are on the ledger. Null when it could not be read. */
	ledger?: UserLedger | null;
	/** Whether they are in the Mailchimp audience, read live from Mailchimp. */
	mailchimp?: UserMailchimp | null;
}

/**
 * added: in the audience with the Wallet tag. missingTag: in the audience, untagged.
 * productionOnly: Mailchimp is only used from production. unavailable: it could not be asked.
 */
export type UserMailchimpStatus = 'added' | 'missingTag' | 'notAdded' | 'productionOnly' | 'noEmail' | 'unavailable';

export interface UserMailchimp {
	status: UserMailchimpStatus;
	/** Mailchimp's own: subscribed, unsubscribed, cleaned or pending. */
	memberStatus?: string;
}

/**
 * A user's ledger registration, done in the background when they are created. Registered
 * once their user record holds the ledger's address for them.
 */
export interface UserLedger {
	registered: boolean;
	blockchainId?: string | null;
	blockchainAddress?: string | null;
	registeredAt?: string | null;
	/** After a retry: whether it registered them, or found them already there. */
	created?: boolean;
}

export interface UserProfile {
	firstName?: string;
	lastName?: string;
	preferredName?: string;
	email: string;
	phone?: string;
	address?: Address;
	latitude?: string;
	longitude?: string;
	currency?: string;
	language?: string;
	dateOfBirth?: string;
	citizenship?: string;
	/** Canonical education history (PTEC-517); most-recent-school is derived, never stored. */
	educationHistory?: EducationHistoryEntry[];
	profilePicture?: BucketImage;
	roles?: UserRole[];
	stripeIdentityVerified?: StripeIdentityVerificationStatus;
}

export interface UserPreview {
	id: string;
	firstName?: string;
	lastName?: string;
	email: string;
	serialNumber?: string;
	externalSerialNumber?: string;
}

export interface AgentPreview {
	organisationId: string;
	userId: string;
	firstName?: string;
	lastName?: string;
	organisationName?: string;
}

export interface InviteUserPayload {
	email: string;
	roles: UserRole[];
	staffInvite?: boolean;
	isAdmin?: boolean;
}

export interface ProspectivePayload {
	email: string;
	organisationId: string;
	type: string;
}

export interface UserPreferences {
	categories: Record<string, BucketImage>;
}

export enum StripeIdentityVerificationStatus {
	NotStarted = 'not_started',
	Started = 'started',
	Processing = 'processing',
	RequiresInput = 'requires_input',
	Verified = 'verified'
}

// Multi-org grant types

export interface PersonalGrant {
	license?: string;
	licenseId?: string;
	walletStorage: number;
	verificationRequests: number;
	candidateEnquiries: number;
	tokenBudget?: number;
	tokenDailyBudget?: number;
	features: string[];
	advisorSessions?: Record<string, number>;
	onboarding: boolean;
	subscriptionExpire?: string;
	gracePeriod?: { ttl: number };
}

export interface OrgGrantAssociation {
	organisationId: string;
	organisationName: string;
	status: 'active' | 'pending';
	joinedDateTime: string;
	grants: {
		walletStorage?: number;
		verificationRequests?: number;
		candidateEnquiries?: number;
		tokenBudget?: number;
		tokenDailyBudget?: number;
		walletCount?: { used: number; total: number };
		staffCount?: number;
		features?: string[];
		advisorSessions?: Record<string, number>;
		license?: string;
	};
}

export interface GrantMetric {
	key: string;
	used: number;
	total: number;
}

export interface UserGrantsResponse {
	personal: PersonalGrant;
	organisations: OrgGrantAssociation[];
	effective: {
		walletStorage: { used: number; total: number };
		verificationRequests: { used: number; total: number };
		candidateEnquiries: { used: number; total: number };
		tokenBudget?: { used: number; total: number };
		tokenDailyBudget?: { used: number; total: number };
		features: string[];
		metrics: GrantMetric[];
		organisationGrantsData: Record<string, any>;
		license?: string;
		subscriptionExpire?: string;
	};
	// Backward-compatible fields
	organisationGrantsData: Record<string, any>;
	metrics: GrantMetric[];
	license?: string;
	licenseId?: string;
	onboarding?: boolean;
	type: 'organisation' | 'personal';
	subscriptionExpire?: string;
	gracePeriod?: { ttl: number };
}
